import api from './api';

/** Diagnostic types, matching the REST path segment used by the backend. */
export type DiagnosticType =
  | 'ping'
  | 'traceroute'
  | 'download'
  | 'upload'
  | 'wifi';

// ─── Generic param shape ──────────────────────────────────────────────────────
export interface DiagnosticParam {
  id: number;
  name?: string;
  variable?: string;
  value: string | null;
  writable?: number;
  options?: string[];
}

/** Shape expected by every POST /diagnostics/{type} body item */
export interface DiagnosticParamPost {
  id: number;
  value: string;
}

// ─── Ping ─────────────────────────────────────────────────────────────────────

export interface PingHistory {
  id: number;
  createdAt: string;
  pingHost: string;
  pingAverageTime: string;
  pingFailureCount: string;
  pingSuccessCount: string;
}

// ─── Download ─────────────────────────────────────────────────────────────────

export interface DownloadHistory {
  id: number;
  createdAt: string;
  downloadUrl: string;
  downloadState: string;
  downloadTotalBytesReceived: string;
  downloadSpeedMbps: number;
}

// ─── Upload ───────────────────────────────────────────────────────────────────

export interface UploadHistory {
  id: number;
  createdAt: string;
  uploadUrl: string;
  uploadState: string;
  uploadTotalBytesSent: string;
  uploadSpeedMbps: number;
}

// ─── Traceroute ─────────────────────────────────────────────────────────────────

export interface TracerouteHopResult {
  hop?: number | string | null;
  hopHost?: string | null;
  hopHostAddress?: string | null;
  hopErrorCode?: number | string | null;
  hopRTTimes?: string | string[] | null;
  host?: string | null;
  address?: string | null;
  name?: string | null;
  latency?: string | string[] | null;
  rtt?: string | string[] | null;
  responseTime?: string | null;
  lat?: number | null;
  lon?: number | null;
  city?: string | null;
  isp?: string | null;
  subdivIso?: string | null;
  [key: string]: any;
}

export interface TracerouteHistory {
  id: number;
  createdAt: string;
  tracerouteHost: string;
  tracerouteState: string;
  tracerouteResponseTime: string;
}

// ─── Combined history ─────────────────────────────────────────────────────────
/** Tool that produced a history entry, as reported by the history endpoint. */
export type DiagnosticHistoryType =
  | 'PING'
  | 'TRACEROUTE'
  | 'DOWNLOAD'
  | 'UPLOAD'
  | 'WIFI';

/** One diagnostic run, uniform across every tool. */
export interface DiagnosticHistoryEntry {
  id: number;
  type: DiagnosticHistoryType;
  result: unknown;
  createdAt: string;
}

// ─── New GET response format ──────────────────────────────────────────────────
/**
 * The GET /diagnostics/{type} endpoint returns an object with semantic keys.
 * Each value is a DiagnosticParam or null.
 */
export interface DiagnosticGetResponse {
  /** Triggers the diagnostic on the device (value: "Requested") */
  status?: DiagnosticParam | null;
  /** DiffServ code point input — always posted as DSCP_POST_VALUE */
  dscp?: DiagnosticParam | null;
  [key: string]: DiagnosticParam | null | undefined;
}

/** DSCP is always sent as 0: diagnostics must not be marked for any DiffServ class. */
export const DSCP_POST_VALUE = '0';

/** Result of getParams: flat array + the ids of the params we always override */
export interface DiagnosticParamsResult {
  params: DiagnosticParam[];
  /** id of the "status/state" param used to trigger the diagnostic (send "Requested") */
  stateParamId: number | null;
  /** id of the "dscp" param, always posted as DSCP_POST_VALUE */
  dscpParamId: number | null;
}

// ─── Service ──────────────────────────────────────────────────────────────────
export const DiagnosticsService = {
  /**
   * GET current saved params for a diagnostic type.
   * Accepts both the legacy array format and the new object format.
   * Always returns a DiagnosticParamsResult with a flat params array,
   * the stateParamId to trigger the diagnostic and the dscpParamId to override.
   */
  getParams: async (
    deviceId: string,
    type: 'ping' | 'download' | 'upload' | 'traceroute'
  ): Promise<DiagnosticParamsResult> => {
    try {
      const response = await api.get<DiagnosticParam[] | DiagnosticGetResponse>(
        `/devices/${deviceId}/diagnostics/${type}`
      );
      const data = response.data;

      // ── New format: object with semantic keys ──────────────────────────────
      if (data && !Array.isArray(data) && typeof data === 'object') {
        const obj = data as DiagnosticGetResponse;
        const stateParamId = obj.status?.id ?? null;
        const dscpParamId = obj.dscp?.id ?? null;
        const params = Object.values(obj).filter(
          (v): v is DiagnosticParam => v != null
        );
        return { params, stateParamId, dscpParamId };
      }

      // ── Legacy format: plain array ─────────────────────────────────────────
      const params = Array.isArray(data) ? data : [];
      return { params, stateParamId: null, dscpParamId: null };
    } catch {
      // 404 = no params saved yet — treat as empty, never redirect
      return { params: [], stateParamId: null, dscpParamId: null };
    }
  },

  /** GET execution history — every diagnostic, or a single type when given. */
  getHistory: async (
    deviceId: string,
    type?: DiagnosticType
  ): Promise<DiagnosticHistoryEntry[]> => {
    const response = await api.get(
      `/devices/${deviceId}/diagnostics/${type ? `${type}/` : ''}history`
    );
    return response.data;
  },

  /** POST — trigger a diagnostic on the device. */
  trigger: async (
    deviceId: string,
    type: DiagnosticType,
    params: DiagnosticParamPost[]
  ): Promise<number> => {
    const response = await api.post(
      `/devices/${deviceId}/diagnostics/${type}`,
      params
    );
    return response.status;
  },

  /** POST — run ping diagnostic */
  ping: (deviceId: string, params: DiagnosticParamPost[]): Promise<number> =>
    DiagnosticsService.trigger(deviceId, 'ping', params),

  /** POST — run download diagnostic */
  download: (
    deviceId: string,
    params: DiagnosticParamPost[]
  ): Promise<number> =>
    DiagnosticsService.trigger(deviceId, 'download', params),

  /** POST — run upload diagnostic */
  upload: (deviceId: string, params: DiagnosticParamPost[]): Promise<number> =>
    DiagnosticsService.trigger(deviceId, 'upload', params),

  /** POST — run traceroute diagnostic */
  traceRoute: (
    deviceId: string,
    params: DiagnosticParamPost[]
  ): Promise<number> =>
    DiagnosticsService.trigger(deviceId, 'traceroute', params),

  /** POST — run wifi scan diagnostic */
  wifi: (deviceId: string, params: DiagnosticParamPost[]): Promise<number> =>
    DiagnosticsService.trigger(deviceId, 'wifi', params),
};

export function findParam(
  params: DiagnosticParam[],
  ...keywords: string[]
): DiagnosticParam | undefined {
  return params.find((p) =>
    keywords.some(
      (kw) =>
        p.variable?.toLowerCase().includes(kw.toLowerCase()) ||
        p.name?.toLowerCase().includes(kw.toLowerCase())
    )
  );
}
